/**
 * A casca do computador (15/10/2026): a área da paciente dentro de um iPhone,
 * sobre o fundo da marca, com um recado ao lado sugerindo o celular. Ver
 * `moldura.ts`.
 *
 * O app roda no quadro, inteiro e sozinho (sessão, dados, análise de uso); a
 * casca só desenha e acompanha o endereço: cada troca de tela lá dentro vira
 * a barra de endereço aqui fora, para recarregar e copiar o link funcionarem.
 * Saiu de `/eu` (sair da conta, login), a janela inteira vai junto.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useBrand } from '@eva/domain';
import { MENSAGEM_DE_ROTA } from './moldura';
import './moldura.css';

/** A tela do iPhone (pontos): a faixa de status em cima, o app no resto. */
const LARGURA = 390;
const ALTURA = 844;
const FAIXA = 44;
const BORDA = 12;

function useEscala(): number {
  const calcular = () => Math.min(1, (window.innerHeight - 48) / (ALTURA + BORDA * 2));
  const [escala, setEscala] = useState(calcular);
  useEffect(() => {
    const aoMudar = () => setEscala(calcular());
    window.addEventListener('resize', aoMudar);
    return () => window.removeEventListener('resize', aoMudar);
  }, []);
  return escala;
}

function horaAgora() {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function MolduraDoCelular() {
  const { brand } = useBrand();
  const escala = useEscala();
  // O endereço que o quadro abre é o desta visita; depois, quem navega é o app lá dentro.
  const inicial = useRef(`${window.location.pathname}${window.location.search}${window.location.hash}`);
  const [hora, setHora] = useState(horaAgora);

  useEffect(() => {
    const t = window.setInterval(() => setHora(horaAgora()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    const aoReceber = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.data?.tipo !== MENSAGEM_DE_ROTA) return;
      const caminho = String(e.data.caminho ?? '');
      if (!caminho.startsWith('/')) return;
      if (caminho === '/eu' || caminho.startsWith('/eu/') || caminho.startsWith('/eu?') || caminho.startsWith('/eu#')) {
        // O título fica o da casca, que já é o da marca (o do quadro pode chegar antes dela).
        window.history.replaceState(null, '', caminho);
      } else {
        // Fora da área da paciente (saiu da conta, login): a janela inteira vai.
        window.location.assign(caminho);
      }
    };
    window.addEventListener('message', aoReceber);
    return () => window.removeEventListener('message', aoReceber);
  }, []);

  const estilo = useMemo(
    () =>
      ({
        '--moldura-escala': escala,
        '--moldura-largura': `${LARGURA}px`,
        '--moldura-altura': `${ALTURA}px`,
        '--moldura-faixa': `${FAIXA}px`,
        '--moldura-borda': `${BORDA}px`,
      }) as CSSProperties,
    [escala]
  );

  return (
    <div className="moldura-tela" style={estilo}>
      <div className="moldura-aparelho-lugar">
        <div className="moldura-aparelho" role="presentation">
          <div className="moldura-status" aria-hidden>
            <span className="moldura-hora">{hora}</span>
            <span className="moldura-ilha" />
            <span className="moldura-icones">
              <span className="moldura-sinal" />
              <span className="moldura-bateria" />
            </span>
          </div>
          <iframe className="moldura-quadro" src={inicial.current} title={brand.name} allow="clipboard-write; fullscreen; autoplay; microphone; camera" />
        </div>
      </div>

      <aside className="moldura-recado">
        <p className="moldura-recado-texto">Acesse pelo seu celular para uma melhor experiência</p>
        <p className="moldura-recado-endereco">{window.location.host}</p>
      </aside>
    </div>
  );
}

